import React, { useEffect, useState } from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Typography } from "@mui/material";
import { InfoOutlined, UploadFileOutlined, WavingHand, RecordVoiceOver } from "@mui/icons-material";
import { useAppDispatch } from "redux/hooks";
import { addSnackbar } from "redux/snackbars/snackbars.slice";
import { t } from "i18n/translates";
import { theme } from "styles/theme";
import { PromptVersionMenu } from "./PromptVersionMenu";
import { ProjectInterface, VoiceBotOptions } from "../types";
import { GreetingEditorDialog } from "./GreetingEditorDialog";
import { TextToSpeechInstructionsEditorDialog } from "./TextToSpeechInstructionsEditorDialog";
import { usePromptVersions } from "../hooks/usePromptVersions";
import Tooltip from "@mui/material/Tooltip";

export const PromptEditor = ({
    project,
    options,
    catchError,
    onPromptChange,
    applyPrompt,
    applyGreeting,
    applyTtsInstructions,
    isStarted,
}: {
    isStarted: boolean;
    project: ProjectInterface;
    options: VoiceBotOptions;
    catchError: (e: Error) => void;
    onPromptChange: (newPrompt: string) => void;
    applyPrompt: () => Promise<void>;
    applyGreeting: (newGreeting: string) => Promise<void>;
    applyTtsInstructions: (instructions: string) => Promise<void>;
}) => {
    const dispatch = useAppDispatch();
    const {
        versions,
        activeVersionId,
        handleAddVersion,
        handleRemoveVersion: removeVersion,
        handleChangeTab,
        handleLabelSave,
        debouncedUpdate,
    } = usePromptVersions();

    const [content, setContent] = useState(options.instructions);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showGreetingEditorDialog, setShowGreetingEditorDialog] = useState(false);
    const [showTtsInstructionsEditorDialog, setShowTtsInstructionsEditorDialog] = useState(false);
    const [isLoading, setLoading] = useState(false);
    const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
    const [showModalInfo, setShowModalInfo] = useState("");

    const handleShowInfo = () => {
        setShowModalInfo(options.defaultInstruction);
    };

    useEffect(() => {
        if (options.instructions !== content) {
            onPromptChange(content);
        }
    }, [content, onPromptChange, options.instructions]);

    useEffect(() => {
        const activeVersion = versions.find((v) => v.id === activeVersionId);
        setContent(activeVersion?.content || "");
    }, [activeVersionId, versions]);

    const handleRemoveVersionWrapper = () => {
        if (deleteTargetId) {
            removeVersion(deleteTargetId);
            setDeleteTargetId(null);
        }
        setShowDeleteDialog(false);
    };

    const handleApplyPrompt = () => {
        setLoading(true);

        onPromptChange(content);

        applyPrompt()
            .then(() => {
                setLoading(false);
                dispatch(
                    addSnackbar({
                        color: "success",
                        title: t("common.saved"),
                    })
                );
            })
            .catch(catchError);
    };

    // Auto-save logic with debounce
    useEffect(() => {
        debouncedUpdate(content);
    }, [content, debouncedUpdate]);

    const openDeleteDialog = (id: string) => {
        setDeleteTargetId(id);
        setShowDeleteDialog(true);
    };

    return (
        <Box sx={{ height: "100%", position: "relative", display: "flex", flexDirection: "column" }}>
            <Box
                sx={{
                    display: "flex",
                    width: "100%",
                    justifyContent: "left",
                    mb: 1,
                    gap: "1em",
                }}
            >
                <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setShowGreetingEditorDialog(true)}
                    startIcon={<WavingHand />}
                >
                    Greetings
                </Button>

                <Button
                    onClick={() => setShowTtsInstructionsEditorDialog(true)}
                    startIcon={<RecordVoiceOver />}
                    size="small"
                    variant="outlined"
                >
                    Voice Prompt
                </Button>
            </Box>
            <Divider />
            <Box
                sx={{
                    display: "flex",
                    width: "100%",
                    justifyContent: "left",
                    mb: 1,
                    mt: 1,
                    gap: "1em",
                }}
            >
                <PromptVersionMenu
                    versions={versions}
                    activeVersionId={activeVersionId}
                    handleSelectVersion={handleChangeTab}
                    handleDeleteVersion={openDeleteDialog}
                    handleSaveLabel={handleLabelSave}
                    handleAddVersion={handleAddVersion}
                />

                <Box
                    sx={{
                        display: "flex",
                        ml: "auto",
                        gap: "1em",
                    }}
                >
                    {project.isRealtime ? (
                        <Button size="small" variant="outlined" onClick={handleShowInfo} startIcon={<InfoOutlined />}>
                            Default prompt
                        </Button>
                    ) : (
                        <Button size="small" variant="outlined" onClick={handleShowInfo} startIcon={<InfoOutlined />}>
                            View the currently saved prompt
                        </Button>
                    )}

                    {project.isRealtime ? (
                        <Tooltip title="This prompt will only affect the current call. You can modify the prompt during an active call.">
                            <Button
                                onClick={handleApplyPrompt}
                                color="success"
                                variant="contained"
                                disabled={isLoading || project.isProtected || !isStarted}
                                startIcon={<UploadFileOutlined />}
                            >
                                Apply
                            </Button>
                        </Tooltip>
                    ) : (
                        <Tooltip title="This prompt will be saved and applied for all users until the bot instance is restarted.">
                            <Button
                                onClick={handleApplyPrompt}
                                color="success"
                                variant="contained"
                                disabled={isLoading || project.isProtected}
                                startIcon={<UploadFileOutlined />}
                            >
                                Upload to Bot
                            </Button>
                        </Tooltip>
                    )}
                </Box>
            </Box>

            <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Type your prompt here..."
                style={{
                    height: "100%",
                    flexGrow: "1",
                    borderRadius: theme.shape.borderRadius,
                    border: `1px solid rgba(0, 0, 0, 0.12)`,
                }}
            />

            <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent dividers>
                    <Typography gutterBottom>Are you sure you want to delete this version?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
                    <Button onClick={handleRemoveVersionWrapper} color="error">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!showModalInfo} onClose={() => setShowModalInfo("")} fullWidth maxWidth="lg">
                <DialogTitle>Current Bot Prompt</DialogTitle>
                <DialogContent dividers>
                    <Typography
                        gutterBottom
                        sx={{
                            whiteSpace: "pre-wrap",
                        }}
                    >
                        {showModalInfo}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="contained"
                        onClick={() => {
                            navigator.clipboard.writeText(showModalInfo).catch(console.error);
                        }}
                    >
                        Copy
                    </Button>
                    <Button onClick={() => setShowModalInfo("")}>Close</Button>
                </DialogActions>
            </Dialog>

            <GreetingEditorDialog
                project={project}
                currentGreeting={options.greeting}
                applyGreeting={applyGreeting}
                catchError={catchError}
                onClose={() => setShowGreetingEditorDialog(false)}
                open={showGreetingEditorDialog}
            />

            <TextToSpeechInstructionsEditorDialog
                project={project}
                currentTtsInstructions={options.ttsInstructions}
                applyTtsInstructions={applyTtsInstructions}
                catchError={catchError}
                onClose={() => setShowTtsInstructionsEditorDialog(false)}
                open={showTtsInstructionsEditorDialog}
            />
        </Box>
    );
};

export default PromptEditor;
